import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SharedNav from '@/components/SharedNav';
import { API } from '@/App';
import {
  ArrowLeft,
  BookOpen,
  Database as DatabaseIcon,
  ListTree,
  Quote,
  AlertTriangle,
  Loader2,
  ArrowUpRight,
} from 'lucide-react';
import ConfidenceChip from '@/components/predict/ConfidenceChip';
import DataSourceLine from '@/components/predict/DataSourceLine';
import ValidationStatus from '@/components/predict/ValidationStatus';
import UncertaintyBand from '@/components/predict/UncertaintyBand';
import DisclaimerBanner from '@/components/predict/DisclaimerBanner';

/**
 * /predict/methodology/:module
 * Sprint 0 — page shell with placeholder content + trust components in their
 * actual context. Once Sprints 1–4 ship the actual modules, the placeholder
 * sections become live computed examples.
 */

const TIER_BADGE = {
  A: { label: 'Tier A · ready to build', bg: '#E6F4EA', fg: '#1B5E20', border: '#A5D6A7' },
  B: { label: 'Tier B · calibration-gated', bg: '#FFF8E1', fg: '#7C5800', border: '#FFE082' },
  C: { label: 'Tier C · refused', bg: '#FCE4EC', fg: '#8B1A4A', border: '#F8BBD0' },
};

export default function Methodology() {
  const { module } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/predict/methodology/${module}`)
      .then((r) => { if (alive) { setMeta(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Failed to load methodology.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [module]);

  if (loading) {
    return (
      <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
        <SharedNav />
        <div className="max-w-[900px] mx-auto py-20 px-6 text-center">
          <Loader2 size={28} className="animate-spin mx-auto" style={{ color: '#2D6A4F' }} />
          <p className="font-body mt-3" style={{ fontSize: 13, color: '#6F6A63' }}>Loading methodology…</p>
        </div>
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
        <SharedNav />
        <div className="max-w-[900px] mx-auto py-20 px-6 text-center">
          <AlertTriangle size={28} className="mx-auto" style={{ color: '#92400E' }} />
          <h1 className="font-display mt-4" style={{ fontSize: 28, color: '#1B4332' }}>Methodology not found</h1>
          <p className="font-body mt-2" style={{ fontSize: 14, color: '#6F6A63' }}>{error || `Unknown module: ${module}`}</p>
          <button
            onClick={() => navigate('/predict')}
            className="font-body mt-6"
            style={{ fontSize: 13, color: '#2D6A4F', textDecoration: 'underline' }}
            data-testid="methodology-back-to-predict"
          >
            ← Back to Predict
          </button>
        </div>
      </div>
    );
  }

  const tier = TIER_BADGE[meta.tier] || TIER_BADGE.A;

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SharedNav />

      <main className="max-w-[920px] mx-auto px-6 pt-10 pb-24" data-testid={`methodology-page-${module}`}>
        {/* Back link */}
        <button
          onClick={() => navigate('/predict')}
          className="inline-flex items-center gap-1.5 mb-6 transition-colors"
          style={{ fontSize: 13, color: '#6F6A63', background: 'transparent', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#2D6A4F'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#6F6A63'; }}
          data-testid="methodology-back"
        >
          <ArrowLeft size={14} /> Back to Predict
        </button>

        {/* Header */}
        <header className="mb-8" data-testid="methodology-header">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span
              style={{
                background: tier.bg,
                color: tier.fg,
                border: `1px solid ${tier.border}`,
                borderRadius: 999,
                padding: '4px 12px',
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {tier.label}
            </span>
            <span
              className="font-body"
              style={{ fontSize: 11.5, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}
            >
              Sprint {meta.sprint}
            </span>
            <ValidationStatus status="pilot-pending" />
          </div>
          <h1 className="font-display" style={{ fontSize: 38, fontWeight: 400, color: '#1B4332', letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: 6 }}>
            {meta.label}
          </h1>
          <p className="font-display" style={{ fontSize: 16, color: '#6F6A63', fontStyle: 'italic' }}>
            Methodology, data sources, citations, and known limitations.
          </p>
        </header>

        <DisclaimerBanner module={`methodology-${module}`} />

        {/* Overview */}
        <Section icon={BookOpen} title="Overview">
          <p className="font-body" style={{ fontSize: 14.5, lineHeight: 1.75, color: '#3F4A3D' }}>
            {meta.methodology_summary}
          </p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <ConfidenceChip
              level={meta.tier === 'A' ? 'high' : meta.tier === 'B' ? 'moderate' : 'low'}
              reason={`Tier ${meta.tier}: ${meta.tier === 'A' ? 'closed-form science, ready to build.' : meta.tier === 'B' ? 'requires local calibration before claiming accuracy.' : 'science cannot honestly support this output today.'}`}
            />
          </div>
        </Section>

        {/* Worked Example (placeholder for Sprint 0) */}
        <Section icon={ListTree} title="Worked example output (preview of Sprint 1+ surface)">
          <div
            className="rounded-xl p-5"
            style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }}
            data-testid="methodology-worked-example"
          >
            <div className="font-body mb-3" style={{ fontSize: 11, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
              Example numeric output as it will appear in the live module
            </div>
            <div className="mb-3">
              {previewExample(module)}
            </div>
            <DataSourceLine
              sources={meta.sources.map((name) => ({ name }))}
              lastUpdated={null}
            />
          </div>
          <p className="font-body mt-3" style={{ fontSize: 12.5, color: '#6F6A63', fontStyle: 'italic' }}>
            Sprint 0 ships only the methodology shell + trust components above.
            The live module ships in Sprint {meta.sprint}.
          </p>
        </Section>

        {/* Data sources */}
        <Section icon={DatabaseIcon} title="Data sources">
          <ul className="font-body" style={{ fontSize: 14, lineHeight: 1.85, color: '#3F4A3D', paddingLeft: 18 }}>
            {meta.sources.map((s) => (
              <li key={s} style={{ marginBottom: 4 }}>{s}</li>
            ))}
          </ul>
        </Section>

        {/* Citations */}
        <Section icon={Quote} title="Citations">
          <ul className="font-body" style={{ fontSize: 13.5, lineHeight: 1.85, color: '#3F4A3D', paddingLeft: 18 }}>
            {meta.citations.map((c) => (
              <li key={c} style={{ marginBottom: 6 }}>{c}</li>
            ))}
          </ul>
        </Section>

        {/* Limitations */}
        <Section icon={AlertTriangle} title="Known limitations" tone="warning">
          <ul className="font-body" style={{ fontSize: 13.5, lineHeight: 1.85, color: '#6B5500', paddingLeft: 18 }}>
            {meta.limitations.map((l) => (
              <li key={l} style={{ marginBottom: 6 }}>{l}</li>
            ))}
          </ul>
        </Section>

        {/* Powered-by inline credibility link */}
        <div
          className="text-center pt-8 mt-6"
          style={{ borderTop: '1px solid #E5E7DD' }}
          data-testid="methodology-powered-by"
        >
          <button
            type="button"
            onClick={() => navigate('/about')}
            className="inline-flex items-center gap-1.5 transition-colors"
            style={{
              fontSize: 12.5,
              color: '#6F6A63',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontStyle: 'italic',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#2D6A4F'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6F6A63'; }}
          >
            Powered by BKAgriD — open agronomy from Agriculture University Jodhpur
            <ArrowUpRight size={11} />
          </button>
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, tone = 'normal', children }) {
  const accent = tone === 'warning' ? '#92400E' : '#2D6A4F';
  return (
    <section className="mb-9" data-testid={`methodology-section-${title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={17} style={{ color: accent }} />
        <h2 className="font-display" style={{ fontSize: 20, color: '#1B4332', fontWeight: 500 }}>
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function previewExample(module) {
  // Different preview per module so the methodology page is concrete.
  if (module === 'water-calculator') {
    return (
      <UncertaintyBand
        value={245}
        unit="mm"
        kind="plus-minus"
        errorPct={12}
        size="lg"
      />
    );
  }
  if (module === 'historical-yield') {
    return (
      <UncertaintyBand
        value={1.8}
        unit="t/ha"
        kind="iqr"
        lower={1.4}
        upper={2.3}
        size="lg"
      />
    );
  }
  if (module === 'pest-tracker') {
    return (
      <UncertaintyBand
        value={342}
        unit="GDD"
        kind="range"
        lower={320}
        upper={360}
        size="lg"
      />
    );
  }
  if (module === 'budget') {
    return (
      <UncertaintyBand
        value={1.42}
        unit="B:C"
        kind="plus-minus"
        errorAbs={0.08}
        size="lg"
      />
    );
  }
  return <UncertaintyBand value="—" kind="class" size="lg" />;
}
